import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend,
} from 'recharts';
import {
  getLLMStats, getLLMHeatmap, getUsageFilters, getUsageBreakdown,
  emailLLMStatsReport, getTopLLMUsers, getDailyByProvider, getLatencyHistogram,
} from '../api';
import { useTheme } from '../contexts/ThemeContext';
import { Mail, BarChart3, SlidersHorizontal } from 'lucide-react';
import UserUsageModal from './UserUsageModal';
import { TokenValue } from '../components/TokenValue';
import FilterDrawer from '../components/FilterDrawer';
import { reportingToday } from '../utils/reportingTime';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const PROVIDER_COLORS = [
  '#f97316', '#3b82f6', '#a855f7', '#22c55e', '#ec4899',
  '#eab308', '#14b8a6', '#ef4444', '#8b5cf6', '#06b6d4',
];

// `daily`/`dailyByProvider` collapse to month buckets ('YYYY-MM') once a
// range has too many days (see _collapse_daily in llm_stats.py); day/week
// keys stay 'YYYY-MM-DD'. Hardcoded month names, not toLocaleDateString —
// Intl's 'short' month for September is locale-dependent ("Sep" vs "Sept").
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDailyId = (id) => {
  if (!id) return id;
  if (/^\d{4}-\d{2}$/.test(id)) {
    const [y, m] = id.split('-');
    return `${MONTH_ABBR[Number(m) - 1]} ${y.slice(2)}`;
  }
  return id.slice(5);
};
// Explicit tick spacing instead of Recharts' width-based default, which skips
// a different number of labels per chart size. The +2 slack avoids jumping
// from "show all" straight to "every 2nd" when count is barely over target.
const tickInterval = (n, target = 7) => (n <= target + 2 ? 0 : Math.max(0, Math.ceil(n / target) - 1));

// ── DateRangeFilter ──────────────────────────────────────────────────────────
// Preset chips + optional custom date inputs. Calls onChange({ days, startDate, endDate }).
const PRESETS = [
  { label: 'Today', days: '1' },
  { label: '7d', days: '7' },
  { label: '30d', days: '30' },
  { label: '90d', days: '90' },
  { label: '1yr', days: '365' },
  { label: 'All Time', days: 'all' },
];

const MODALITY_LABEL = {
  chat: 'Chat', transcription: 'Transcription', speech: 'Speech', realtime: 'Live sessions',
};

function DateRangeFilter({ value, onChange }) {
  const { theme } = useTheme();
  // Keep the input max aligned when the page remains open across IST midnight.
  const todayStr = reportingToday();
  // Seeded from the applied filter, not blank - the parent remounts this
  // component on every loadData() (it swaps to a full-page loading state),
  // so starting from '' every time wiped the picked dates right after they
  // were applied even though the query itself still used them correctly.
  const [customStart, setCustomStart] = useState(value.startDate || '');
  const [customEnd, setCustomEnd] = useState(value.endDate || '');
  const [rangeError, setRangeError] = useState('');

  const handlePreset = (days) => {
    setCustomStart('');
    setCustomEnd('');
    setRangeError('');
    onChange({ days, startDate: null, endDate: null });
  };

  const handleCustomChange = (start, end) => {
    if (start && end) {
      if (start > end) {
        setRangeError('Start date must be on or before the end date.');
        return;
      }
      setRangeError('');
      onChange({ days: null, startDate: start, endDate: end });
    } else if (!start && !end) {
      setRangeError('');
      onChange({ days: value.days || '7', startDate: null, endDate: null });
    }
  };

  const chip = (active) =>
    `px-2 py-1 rounded-lg text-xs font-medium border transition-colors ${
      active
        ? 'bg-orange text-white border-orange'
        : 'bg-field border-field-border text-fg hover:border-orange/50'
    }`;

  const isCustomActive = value.startDate && value.endDate;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {PRESETS.map((p) => (
        <button
          key={p.days}
          onClick={() => handlePreset(p.days)}
          className={chip(!isCustomActive && value.days === p.days)}
        >
          {p.label}
        </button>
      ))}
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
        rangeError ? 'border-red-500 bg-red-500/10' : isCustomActive ? 'border-orange bg-orange/10' : 'border-field-border bg-field'
      }`}>
        <input
          type="date"
          value={customStart}
          max={customEnd || todayStr}
          onChange={(e) => {
            const newStart = e.target.value;
            setCustomStart(newStart);
            // Clearing Start disables the End input below — clear its stale
            // value too, instead of leaving it stuck showing an old date.
            const end = newStart ? customEnd : '';
            if (!newStart) setCustomEnd('');
            handleCustomChange(newStart, end);
          }}
          style={{ colorScheme: theme === 'light' ? 'light' : 'dark' }}
          className="ls-date-input bg-transparent text-fg text-xs outline-none w-28"
          placeholder="Start"
        />
        <span className="text-fg-muted text-xs">→</span>
        <input
          type="date"
          value={customEnd}
          min={customStart || undefined}
          max={todayStr}
          disabled={!customStart}
          onChange={(e) => {
            setCustomEnd(e.target.value);
            handleCustomChange(customStart, e.target.value);
          }}
          style={{ colorScheme: theme === 'light' ? 'light' : 'dark' }}
          className="ls-date-input bg-transparent text-fg text-xs outline-none w-28 disabled:opacity-40 disabled:cursor-not-allowed"
          placeholder="End"
        />
      </div>
      {rangeError && <span className="text-red-500 text-xs">{rangeError}</span>}
    </div>
  );
}

// ── UsageExplorer ────────────────────────────────────────────────────────────
function UsageExplorer({ dateFilter, model, userId, product }) {
  const [filters, setFilters] = useState({ providers: [], projects: [] });
  const [by, setBy] = useState('model');
  const [provider, setProvider] = useState('');
  const [projectId, setProjectId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // user drill-down modal
  const [selectedUser, setSelectedUser] = useState(null); // {user_id, user_email_id}

  useEffect(() => { getUsageFilters().then((r) => setFilters(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const params = { by };
    if (dateFilter.startDate && dateFilter.endDate) {
      params.start_date = dateFilter.startDate;
      params.end_date = dateFilter.endDate;
    } else {
      const d = dateFilter.days === 'all' ? null : parseInt(dateFilter.days);
      if (d) params.days = d;
    }
    if (provider) params.provider = provider;
    if (projectId) params.project_id = projectId;
    if (model) params.model = model;
    if (userId) params.user_id = userId;
    if (product) params.product = product;
    getUsageBreakdown(params)
      .then((r) => setRows(r.data.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [by, provider, projectId, dateFilter, model, userId, product]);

  const sel = 'px-3 py-1.5 bg-field border border-field-border rounded-lg text-fg text-sm';
  const maxCost = Math.max(...rows.map((r) => r.cost), 0.000001);
  const showLatency = by !== 'user';
  const dimLabel = { model: 'Model', user: 'User', provider: 'Provider', project: 'Project' }[by];

  return (
    <>
    <div className="bg-panel border border-panel-border rounded-xl p-6 mb-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-fg">Usage Explorer</h2>
          <p className="text-fg-muted text-xs">
            Group by model / user / provider / project — filter by a provider and/or project.
            {by === 'user' && <span className="text-orange ml-1">Click a user row to see full analytics.</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-fg-muted">Group by</span>
          <select value={by} onChange={(e) => setBy(e.target.value)} className={sel}>
            <option value="model">Model</option>
            <option value="user">User</option>
            <option value="provider">Provider</option>
            <option value="project">Project</option>
          </select>
          <select value={provider} onChange={(e) => setProvider(e.target.value)} className={sel}>
            <option value="">All providers</option>
            {filters.providers.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className={sel}>
            <option value="">All projects</option>
            {filters.projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name || p.project_id.slice(0, 8)}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-fg-muted text-sm py-8 text-center">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-fg-muted text-sm py-8 text-center">No usage for this filter.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-fg-muted border-b border-panel-border">
                <th className="py-2 pr-3 font-medium">{dimLabel}</th>
                <th className="py-2 pr-3 font-medium text-right">Requests</th>
                <th className="py-2 pr-3 font-medium text-right">Cost</th>
                <th className="py-2 pr-3 font-medium text-right">Input tok</th>
                <th className="py-2 pr-3 font-medium text-right">Output tok</th>
                <th className="py-2 pr-3 font-medium text-right">Cache read tok</th>
                <th className="py-2 pr-3 font-medium text-right">Cache write tok</th>
                {showLatency && <th className="py-2 font-medium text-right">Avg latency</th>}
                {by === 'user' && <th className="py-2 font-medium text-right w-8"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.key}
                  className={`border-b border-panel-border/50 ${by === 'user' ? 'hover:bg-fg/5 cursor-pointer' : ''}`}
                  onClick={() => by === 'user' && setSelectedUser({ user_id: r.key, user_email_id: r.label })}
                >
                  <td className="py-2 pr-3">
                    <div className="text-fg truncate max-w-[260px]" title={r.label}>{r.label}</div>
                    <div className="h-1 mt-1 rounded-full bg-orange/70" style={{ width: `${(r.cost / maxCost) * 100}%`, maxWidth: '240px' }} />
                  </td>
                  <td className="py-2 pr-3 text-right text-fg"><TokenValue value={r.requests} unit="requests" /></td>
                  <td className="py-2 pr-3 text-right text-green-400"><TokenValue value={r.cost} currency /></td>
                  <td className="py-2 pr-3 text-right text-fg-muted"><TokenValue value={r.input_tokens} /></td>
                  <td className="py-2 pr-3 text-right text-fg-muted"><TokenValue value={r.output_tokens} /></td>
                  <td className="py-2 pr-3 text-right text-fg-muted"><TokenValue value={r.cache_read_tokens} /></td>
                  <td className="py-2 pr-3 text-right text-fg-muted"><TokenValue value={r.cache_write_tokens} /></td>
                  {showLatency && <td className="py-2 text-right text-fg-muted">{r.avg_latency_ms ? `${Math.round(r.avg_latency_ms)}ms` : '—'}</td>}
                  {by === 'user' && (
                    <td className="py-2 text-right">
                      <BarChart3 className="w-3.5 h-3.5 text-fg-muted inline-block" />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>

    {/* User usage drawer — opens when a user row is clicked */}
    {selectedUser && (
      <UserUsageModal
        user={selectedUser}
        onClose={() => setSelectedUser(null)}
      />
    )}
    </>
  );
}

// ── RequestHeatmap ───────────────────────────────────────────────────────────
function RequestHeatmap({ cells, maxRequests }) {
  const [hover, setHover] = useState(null);
  const grid = {};
  (cells || []).forEach((c) => { grid[`${c.dow}-${c.hour}`] = c; });
  const hours = Array.from({ length: 24 }, (_, h) => h);

  const dayTotals = DOW.map((_, d) => {
    let requests = 0, cost = 0;
    hours.forEach((h) => { const c = grid[`${d}-${h}`]; if (c) { requests += c.requests; cost += c.cost; } });
    return { requests, cost };
  });

  return (
    <div>
      <div className="mb-3 min-h-[1.5rem] text-sm">
        {hover ? (
          <span className="text-fg">
            {hover.label} {hover.hour != null ? `${hover.hour}:00–${hover.hour}:59 IST` : '· whole day'} —{' '}
            <span className="text-orange font-semibold">{hover.requests.toLocaleString()}</span> requests ·{' '}
            <span className="text-green-400">${hover.cost.toFixed(4)}</span>
          </span>
        ) : (
          <span className="text-fg-muted">Hover a cell for an hour, or a day label for the whole day.</span>
        )}
      </div>
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          <div className="flex">
            <div className="w-10" />
            {hours.map((h) => (
              <div key={h} className="w-5 text-center text-[9px] text-fg-muted">{h % 3 === 0 ? h : ''}</div>
            ))}
          </div>
          {DOW.map((label, d) => (
            <div key={d} className="flex items-center">
              <div
                className="w-10 text-[10px] text-fg-muted pr-1 text-right cursor-pointer hover:text-fg"
                onMouseEnter={() => setHover({ label, hour: null, requests: dayTotals[d].requests, cost: dayTotals[d].cost })}
                onMouseLeave={() => setHover(null)}
              >{label}</div>
              {hours.map((h) => {
                const cell = grid[`${d}-${h}`];
                const n = cell ? cell.requests : 0;
                const intensity = maxRequests ? Math.min(1, n / maxRequests) : 0;
                const bg = n === 0 ? 'rgba(128,128,128,0.12)' : `rgba(249,115,22,${0.12 + intensity * 0.88})`;
                const active = hover && hover.label === label && hover.hour === h;
                return (
                  <div
                    key={h}
                    className={`w-5 h-5 m-[1px] rounded-sm cursor-pointer transition-shadow ${active ? 'ring-2 ring-white' : ''}`}
                    onMouseEnter={() => setHover({ label, hour: h, requests: n, cost: cell ? cell.cost : 0 })}
                    onMouseLeave={() => setHover(null)}
                    style={{ backgroundColor: bg }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main LLMStats page ───────────────────────────────────────────────────────
function LLMStats() {
  const { theme } = useTheme();
  const axisColor = theme === 'light' ? '#6b7280' : '#9aa0aa';
  const tooltipStyle = {
    background: theme === 'light' ? '#ffffff' : '#141414',
    border: `1px solid ${theme === 'light' ? '#e5e7eb' : '#2a2a2a'}`,
    borderRadius: '8px',
    color: theme === 'light' ? '#111827' : '#f5f5f5',
  };
  const tooltipLabelStyle = { color: theme === 'light' ? '#111827' : '#f5f5f5' };

  const [dateFilter, setDateFilter] = useState({ days: '7', startDate: null, endDate: null });
  const [model, setModel] = useState('');
  const [userId, setUserId] = useState('');
  const [product, setProduct] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);

  const [data, setData] = useState(null);
  const [heatmap, setHeatmap] = useState({ cells: [], max_requests: 0 });
  const [topUsers, setTopUsers] = useState([]);
  const [dailyByProvider, setDailyByProvider] = useState([]);
  const [latencyBuckets, setLatencyBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);

  // Filter drawer: edits are held as a draft and only take effect on Apply,
  // so adjusting filters doesn't refetch on every keystroke/click.
  const [filterOpen, setFilterOpen] = useState(false);

  // Badge count: applied filters that differ from the default (7d, all models/users/products).
  const activeFilterCount =
    (model ? 1 : 0) +
    (userId ? 1 : 0) +
    (product ? 1 : 0) +
    ((dateFilter.startDate && dateFilter.endDate) || dateFilter.days !== '7' ? 1 : 0);

  // Load model list once
  useEffect(() => {
    getUsageFilters()
      .then((r) => {
        setAvailableModels(r.data.models || []);
        setAvailableUsers(r.data.users || []);
        setAvailableProducts(r.data.products || []);
      })
      .catch(() => {});
  }, []);

  const getApiParams = useCallback(() => {
    if (dateFilter.startDate && dateFilter.endDate) {
      return { days: null, startDate: dateFilter.startDate, endDate: dateFilter.endDate };
    }
    const days = dateFilter.days === 'all' ? null : parseInt(dateFilter.days);
    return { days, startDate: null, endDate: null };
  }, [dateFilter]);

  const sendReport = async () => {
    setEmailing(true);
    setEmailMsg(null);
    try {
      const { days, startDate, endDate } = getApiParams();
      const res = await emailLLMStatsReport(days, startDate, endDate);
      setEmailMsg({ ok: true, text: `Report is on its way to ${res.data.sent_to} — it'll arrive shortly.` });
    } catch (e) {
      setEmailMsg({ ok: false, text: e.response?.data?.detail || 'Failed to send email' });
    } finally {
      setEmailing(false);
      setTimeout(() => setEmailMsg(null), 5000);
    }
  };

  useEffect(() => {
    loadData();
  }, [dateFilter, model, userId, product]);

  const loadData = async () => {
    setLoading(true);
    try {
      const { days, startDate, endDate } = getApiParams();
      const selectedModel = model || null;
      const selectedUser = userId || null;
      const selectedProduct = product || null;
      const [statsRes, heatRes, usersRes, providerRes, latencyRes] = await Promise.all([
        getLLMStats(days, selectedModel, startDate, endDate, selectedUser, selectedProduct),
        getLLMHeatmap(days, selectedModel, startDate, endDate, selectedUser, selectedProduct),
        getTopLLMUsers(days, 10, startDate, endDate, selectedUser, selectedProduct),
        getDailyByProvider(days, startDate, endDate, selectedUser, selectedProduct),
        getLatencyHistogram(days, selectedModel, startDate, endDate, selectedUser, selectedProduct),
      ]);
      setData(statsRes.data);
      setHeatmap({ cells: heatRes.data.cells || [], max_requests: heatRes.data.max_requests || 0 });
      setTopUsers(usersRes.data.top_users || []);

      // Reshape daily-by-provider into [{date, ProviderA: cost, ProviderB: cost, ...}]
      const provRows = providerRes.data.rows || [];
      const providerSet = [...new Set(provRows.map((r) => r.provider))];
      const dateMap = {};
      provRows.forEach((r) => {
        if (!dateMap[r.date]) dateMap[r.date] = { date: r.date };
        dateMap[r.date][r.provider] = r.cost;
      });
      setDailyByProvider({ rows: Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date)), providers: providerSet });

      setLatencyBuckets(latencyRes.data.buckets || []);
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-fg-muted">Loading...</div>
      </div>
    );
  }

  const overall = data?.overall || {};
  const avgCostPerReq = overall.total_requests > 0
    ? (overall.total_cost / overall.total_requests)
    : 0;
  // hits / (hits + misses), where a cache write IS the miss — the prefix was
  // absent or expired, so it had to be written. `input_tokens` is deliberately
  // excluded: those fall after the last cache breakpoint and were never cache
  // candidates, so they are not misses. Counting the writes is what matters —
  // without them this read ~100% for any warm session.
  const cacheLookups = (overall.total_cache_read_tokens || 0) + (overall.total_cache_write_tokens || 0);
  const cacheHitRate = cacheLookups > 0
    ? ((overall.total_cache_read_tokens / cacheLookups) * 100)
    : 0;
  // Avg cost per request, split by token type (backend derives type cost from pricing).
  const reqs = overall.total_requests || 0;
  const avgInputCost      = reqs ? (overall.total_input_cost || 0) / reqs : 0;
  const avgOutputCost     = reqs ? (overall.total_output_cost || 0) / reqs : 0;
  const avgCacheReadCost  = reqs ? (overall.total_cache_read_cost || 0) / reqs : 0;
  const avgCacheWriteCost = reqs ? (overall.total_cache_write_cost || 0) / reqs : 0;

  const sel = 'px-3 py-1.5 bg-field border border-field-border rounded-lg text-fg text-sm';

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <h1 className="text-2xl font-bold text-fg">Usage Analytics</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFilterOpen(true)}
            className="relative flex items-center gap-2 px-3 py-2 bg-field border border-field-border hover:border-orange/50 text-fg rounded-lg text-sm transition-colors"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-5 h-5 px-1.5 bg-orange text-white text-xs font-semibold rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            onClick={sendReport}
            disabled={emailing}
            className="flex items-center gap-2 px-3 py-2 bg-orange hover:bg-orange-hover text-fg rounded-lg text-sm disabled:opacity-50"
          >
            <Mail className="w-4 h-4" />
            {emailing ? 'Sending…' : 'Email report'}
          </button>
        </div>
      </div>

      {emailMsg && (
        <div className={`mb-4 p-3 rounded-lg text-sm flex justify-between ${emailMsg.ok ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
          <span>{emailMsg.text}</span>
          <button onClick={() => setEmailMsg(null)}>✕</button>
        </div>
      )}

      {/* Overview Stats — 10 cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <div className="bg-panel border border-panel-border rounded-xl p-5">
          <p className="text-fg-muted text-xs mb-1">Total Cost</p>
          <p className="text-2xl font-bold text-green-500">${overall.total_cost?.toFixed(2)}</p>
        </div>
        <div className="bg-panel border border-panel-border rounded-xl p-5">
          <p className="text-fg-muted text-xs mb-1">Total Requests</p>
          <p className="text-2xl font-bold text-orange">{overall.total_requests?.toLocaleString()}</p>
        </div>
        <div className="bg-panel border border-panel-border rounded-xl p-5">
          <p className="text-fg-muted text-xs mb-1">Input Tokens</p>
          <p className="text-2xl font-bold text-blue-500">{((overall.total_input_tokens || 0) / 1e6).toFixed(2)}M</p>
        </div>
        <div className="bg-panel border border-panel-border rounded-xl p-5">
          <p className="text-fg-muted text-xs mb-1">Output Tokens</p>
          <p className="text-2xl font-bold text-purple-500">{((overall.total_output_tokens || 0) / 1e6).toFixed(2)}M</p>
        </div>
        <div className="bg-panel border border-panel-border rounded-xl p-5">
          <p className="text-fg-muted text-xs mb-1">Avg Cost / Req</p>
          <p className="text-2xl font-bold text-yellow-400">${avgCostPerReq.toFixed(4)}</p>
        </div>
        <div className="bg-panel border border-panel-border rounded-xl p-5">
          <p className="text-fg-muted text-xs mb-1">Cache Hit Rate</p>
          <p className="text-2xl font-bold text-teal-400">{cacheHitRate.toFixed(1)}%</p>
        </div>
        <div className="bg-panel border border-panel-border rounded-xl p-5">
          <p className="text-fg-muted text-xs mb-1">Avg Input Cost / Req</p>
          <p className="text-2xl font-bold text-blue-400">${avgInputCost.toFixed(4)}</p>
        </div>
        <div className="bg-panel border border-panel-border rounded-xl p-5">
          <p className="text-fg-muted text-xs mb-1">Avg Output Cost / Req</p>
          <p className="text-2xl font-bold text-purple-400">${avgOutputCost.toFixed(4)}</p>
        </div>
        <div className="bg-panel border border-panel-border rounded-xl p-5">
          <p className="text-fg-muted text-xs mb-1">Avg Cache-Read Cost / Req</p>
          <p className="text-2xl font-bold text-green-400">${avgCacheReadCost.toFixed(4)}</p>
        </div>
        <div className="bg-panel border border-panel-border rounded-xl p-5">
          <p className="text-fg-muted text-xs mb-1">Avg Cache-Write Cost / Req</p>
          <p className="text-2xl font-bold text-yellow-500">${avgCacheWriteCost.toFixed(4)}</p>
        </div>
      </div>

      {/* Token breakdown */}
      <div className="bg-panel border border-panel-border rounded-xl p-6 mb-8">
        <h2 className="text-lg font-semibold text-fg mb-4">Token breakdown (cost drivers)</h2>
        {(() => {
          const parts = [
            { k: 'Input', v: overall.total_input_tokens || 0, c: '#3b82f6' },
            { k: 'Output', v: overall.total_output_tokens || 0, c: '#a855f7' },
            { k: 'Cache read', v: overall.total_cache_read_tokens || 0, c: '#22c55e' },
            { k: 'Cache write', v: overall.total_cache_write_tokens || 0, c: '#eab308' },
            { k: 'Reasoning', v: overall.total_reasoning_tokens || 0, c: '#ec4899' },
          ];
          const total = parts.reduce((s, p) => s + p.v, 0) || 1;
          return (
            <>
              <div className="flex h-3 rounded-full overflow-hidden mb-4 bg-fg/5">
                {parts.map((p) => p.v > 0 && (
                  <div key={p.k} style={{ width: `${(p.v / total) * 100}%`, backgroundColor: p.c }}
                    title={`${p.k}: ${p.v.toLocaleString()}`} />
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {parts.map((p) => (
                  <div key={p.k}>
                    <div className="flex items-center gap-1.5 text-xs text-fg-muted">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.c }} />{p.k}
                    </div>
                    <div className="text-fg text-sm font-semibold mt-0.5">{(p.v / 1e6).toFixed(2)}M</div>
                    <div className="text-[11px] text-fg-muted">{((p.v / total) * 100).toFixed(1)}%</div>
                  </div>
                ))}
              </div>
            </>
          );
        })()}
      </div>

      {/* Requests heatmap */}
      <div className="bg-panel border border-panel-border rounded-xl p-6 mb-8">
        <h2 className="text-lg font-semibold text-fg mb-1">Requests heatmap</h2>
        <p className="text-fg-muted text-xs mb-4">Requests by hour ({heatmap.reporting_timezone || 'IST'}) × day of week — hover a cell for counts &amp; cost.</p>
        <RequestHeatmap cells={heatmap.cells} maxRequests={heatmap.max_requests} />
      </div>

      {/* Usage Explorer */}
      <UsageExplorer dateFilter={dateFilter} model={model} userId={userId} product={product} />

      {/* Daily charts — Cost + Requests side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-panel border border-panel-border rounded-xl p-6">
          <h2 className="text-lg font-semibold text-fg mb-4">Daily Cost</h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.daily || []} margin={{ top: 5, right: 18, left: 0, bottom: 5 }}>
                <XAxis dataKey="date" stroke={axisColor} fontSize={12} tickFormatter={formatDailyId} interval={tickInterval((data?.daily || []).length)} />
                <YAxis stroke={axisColor} fontSize={12} tickFormatter={(v) => `$${v}`} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} labelFormatter={formatDailyId} formatter={(v) => [`$${v.toFixed(4)}`, 'Cost']} />
                <Line type="monotone" dataKey="cost" stroke="#f97316" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-panel border border-panel-border rounded-xl p-6">
          <h2 className="text-lg font-semibold text-fg mb-4">Daily Requests</h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.daily || []} margin={{ top: 5, right: 18, left: 0, bottom: 5 }}>
                <XAxis dataKey="date" stroke={axisColor} fontSize={12} tickFormatter={formatDailyId} interval={tickInterval((data?.daily || []).length)} />
                <YAxis stroke={axisColor} fontSize={12} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} labelFormatter={formatDailyId} formatter={(v) => [v.toLocaleString(), 'Requests']} />
                <Line type="monotone" dataKey="requests" stroke="#3b82f6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Cost Trend by Provider */}
      {dailyByProvider.rows?.length > 0 && (
        <div className="bg-panel border border-panel-border rounded-xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-fg mb-4">Cost Trend by Provider</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyByProvider.rows} margin={{ top: 5, right: 18, left: 0, bottom: 5 }}>
                <XAxis dataKey="date" stroke={axisColor} fontSize={12} tickFormatter={formatDailyId} interval={tickInterval(dailyByProvider.rows.length, 12)} />
                <YAxis stroke={axisColor} fontSize={12} tickFormatter={(v) => `$${v}`} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} labelFormatter={formatDailyId} formatter={(v) => [`$${Number(v).toFixed(4)}`]} />
                <Legend wrapperStyle={{ fontSize: '12px', color: axisColor }} />
                {(dailyByProvider.providers || []).map((p, i) => (
                  <Line
                    key={p}
                    type="monotone"
                    dataKey={p}
                    stroke={PROVIDER_COLORS[i % PROVIDER_COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Model charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Cost by Model */}
        <div className="bg-panel border border-panel-border rounded-xl p-6">
          <h2 className="text-lg font-semibold text-fg mb-4">Cost by Model</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.by_model?.slice(0, 8) || []} layout="vertical">
                <XAxis type="number" stroke={axisColor} fontSize={12} tickFormatter={(v) => `$${v}`} />
                <YAxis type="category" dataKey="model" stroke={axisColor} fontSize={10} width={120} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`$${v.toFixed(4)}`, 'Cost']} />
                <Bar dataKey="cost" fill="#f97316" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Requests by Model */}
        <div className="bg-panel border border-panel-border rounded-xl p-6">
          <h2 className="text-lg font-semibold text-fg mb-4">Requests by Model</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.by_model?.slice(0, 8) || []} layout="vertical">
                <XAxis type="number" stroke={axisColor} fontSize={12} />
                <YAxis type="category" dataKey="model" stroke={axisColor} fontSize={10} width={120} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v.toLocaleString(), 'Requests']} />
                <Bar dataKey="requests" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Avg Latency by Model */}
        <div className="bg-panel border border-panel-border rounded-xl p-6">
          <h2 className="text-lg font-semibold text-fg mb-4">Avg Latency by Model</h2>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {data?.by_model?.slice(0, 10).map((item, i) => {
              const latency = item.avg_latency_ms || 0;
              const maxLatency = Math.max(...(data?.by_model?.map(m => m.avg_latency_ms || 0) || [1]));
              return (
                <div key={i}>
                  <div className="flex justify-between mb-1">
                    <span className="text-fg text-sm truncate max-w-[150px]" title={item.model}>{item.model}</span>
                    <span className={`text-sm font-medium ${latency > 5000 ? 'text-red-400' : latency > 2000 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {latency > 1000 ? `${(latency / 1000).toFixed(1)}s` : `${Math.round(latency)}ms`}
                    </span>
                  </div>
                  <div className="h-2 bg-surface rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${latency > 5000 ? 'bg-red-500' : latency > 2000 ? 'bg-yellow-500' : 'bg-green-500'}`}
                      style={{ width: `${maxLatency > 0 ? (latency / maxLatency) * 100 : 0}%` }}
                    />
                  </div>
                  <p className="text-fg-muted text-xs mt-1">{item.requests.toLocaleString()} requests</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Latency Distribution */}
        {latencyBuckets.length > 0 && (
          <div className="bg-panel border border-panel-border rounded-xl p-6">
            <h2 className="text-lg font-semibold text-fg mb-4">Latency Distribution</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={latencyBuckets}>
                  <XAxis dataKey="label" stroke={axisColor} fontSize={11} />
                  <YAxis stroke={axisColor} fontSize={12} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    labelStyle={tooltipLabelStyle}
                    formatter={(v) => [v.toLocaleString(), 'Requests']}
                  />
                  <Bar dataKey="count" fill="#a855f7" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Cost by Provider */}
      <div className="bg-panel border border-panel-border rounded-xl p-6 mb-8">
        <h2 className="text-lg font-semibold text-fg mb-4">Cost by Provider</h2>
        <div className="space-y-4">
          {data?.by_provider?.map((item, i) => (
            <div key={i}>
              <div className="flex justify-between mb-1">
                <span className="text-fg">{item.provider}</span>
                <span className="text-green-500">${item.cost.toFixed(4)}</span>
              </div>
              <div className="h-2 bg-surface rounded-full overflow-hidden">
                <div
                  className="h-full bg-orange rounded-full"
                  style={{ width: `${(item.cost / (overall.total_cost || 1)) * 100}%` }}
                />
              </div>
              <p className="text-fg-muted text-xs mt-1">{item.requests.toLocaleString()} requests</p>
            </div>
          ))}
        </div>
      </div>

      {/* Cost by modality: shown once anything but chat has run */}
      {data?.by_modality?.some((m) => m.modality !== 'chat') && (
        <div className="bg-panel border border-panel-border rounded-xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-fg mb-4">Cost by Modality</h2>
          <div className="space-y-4">
            {data.by_modality.map((item) => (
              <div key={item.modality}>
                <div className="flex justify-between mb-1">
                  <span className="text-fg">{MODALITY_LABEL[item.modality] ?? item.modality}</span>
                  <span className="text-green-500">${item.cost.toFixed(4)}</span>
                </div>
                <div className="h-2 bg-surface rounded-full overflow-hidden">
                  <div
                    className="h-full bg-orange rounded-full"
                    style={{ width: `${(item.cost / (overall.total_cost || 1)) * 100}%` }}
                  />
                </div>
                <p className="text-fg-muted text-xs mt-1">
                  {item.requests.toLocaleString()} {item.modality === 'realtime' ? 'sessions' : 'requests'}
                  {item.audio_seconds > 0 && ` · ${(item.audio_seconds / 60).toFixed(1)} min of audio`}
                  {item.live_seconds > 0 && ` · ${(item.live_seconds / 60).toFixed(1)} min live`}
                  {item.estimated_usd > 0 && ` · $${item.estimated_usd.toFixed(4)} estimated`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Users */}
      {topUsers.length > 0 && (
        <div className="bg-panel border border-panel-border rounded-xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-fg mb-4">Top Users by Cost</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-fg-muted border-b border-panel-border">
                  <th className="py-2 pr-3 font-medium w-8">#</th>
                  <th className="py-2 pr-3 font-medium">User</th>
                  <th className="py-2 pr-3 font-medium text-right">Requests</th>
                  <th className="py-2 pr-3 font-medium text-right">Cost</th>
                  <th className="py-2 font-medium">Projects</th>
                </tr>
              </thead>
              <tbody>
                {topUsers.map((u, i) => (
                  <tr key={u.user_id} className="border-b border-panel-border/50">
                    <td className="py-2 pr-3 text-fg-muted">{i + 1}</td>
                    <td className="py-2 pr-3 text-fg truncate max-w-[200px]" title={u.email}>{u.email}</td>
                    <td className="py-2 pr-3 text-right text-fg">{u.requests.toLocaleString()}</td>
                    <td className="py-2 pr-3 text-right text-green-400">${u.cost.toFixed(4)}</td>
                    <td className="py-2 text-fg-muted text-xs truncate max-w-[200px]">{u.projects.join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filter drawer — shared component; keeps this page's DateRangeFilter (always-visible
          custom range) via renderTimeframe, and adds the Model / User / Product selects. */}
      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        value={{ dateFilter, model, product, user: userId }}
        selects={[
          { key: 'model', label: 'Model', allLabel: 'All models', options: availableModels },
          { key: 'user', label: 'User', allLabel: 'All users',
            options: availableUsers.map((u) => ({ value: u.user_id, label: u.email })) },
          { key: 'product', label: 'Product', allLabel: 'All products', options: availableProducts },
        ]}
        renderTimeframe={(tf, setTf, isOpen) => (
          <DateRangeFilter key={isOpen ? 'open' : 'closed'} value={tf} onChange={setTf} />
        )}
        onApply={(next) => { setDateFilter(next.dateFilter); setModel(next.model); setUserId(next.user); setProduct(next.product); }}
      />
    </div>
  );
}

export default LLMStats;
